import React, { useState } from "react";
import { motion } from "motion/react";
import { useNavigate } from "react-router-dom";
import {
    FiUser,
    FiMail,
    FiMessageSquare,
    FiArrowRight,
    FiArrowLeft,
    FiCheck,
    FiZap,
    FiHeart,
    FiSend,
} from "react-icons/fi";
import { HiOutlineLightBulb } from "react-icons/hi";
import { HiSparkles } from "react-icons/hi2";
import ClientCalling from "../api/ClientCalling";
import clientToast from "../components/Client/clientToast";
import ClientNavbar from "../components/Navbar/ClinetNavbar";
import FixedButton from "../components/Client/FixedButton";
import ClientFooter from "../components/Client/ClientFooter";
import ClientAdSense from "../components/Client/ClientAdSense";

const FEATURES = [
    {
        icon: FiZap,
        title: "We read everything",
        desc: "Every idea, bug report, and feature request gets reviewed.",
    },
    {
        icon: FiHeart,
        title: "Built by feedback",
        desc: "Many of our best features started as suggestions from users.",
    },
    {
        icon: HiOutlineLightBulb,
        title: "Spark new prompts",
        desc: "Suggest themes or categories — we'll add them to the library.",
    },
];

const ClientSuggestion = () => {
    const navigate = useNavigate();
    const [sendData, setSendData] = useState({
        name: "",
        email: "",
        msg: "",
    });
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setSendData((prev) => ({ ...prev, [name]: value }));
    };

    const validate = () => {
        if (!sendData.name || sendData.name.length < 3 || sendData.name.length > 50) {
            clientToast.error("Name must be 3 to 50 characters");
            return false;
        }
        if (!sendData.email || !/^\S+@\S+\.\S+$/.test(sendData.email)) {
            clientToast.error("Please enter a valid email");
            return false;
        }
        if (!sendData.msg || sendData.msg.length < 3 || sendData.msg.length > 250) {
            clientToast.error("Message must be 3 to 250 characters");
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        try {
            setLoading(true);
            const resp = await ClientCalling.sendSuggestion(sendData);
            if (resp?.status) {
                clientToast.success("Suggestion sent successfully!");
                setSent(true);
                setSendData({ name: "", email: "", msg: "" });
            } else {
                clientToast.error(resp?.message || "Failed to send suggestion");
            }
        } catch (err) {
            console.error(err);
            clientToast.error("Server error. Please try again later.");
        } finally {
            setLoading(false);
        }
    };

    const charCount = sendData.msg.length;
    const charPct = Math.min(100, (charCount / 250) * 100);

    return (
        <div className='min-h-screen dm-sans client-home-bg'>
            <ClientNavbar />
            <FixedButton />

            {/* HERO */}
            <section className='relative w-full overflow-hidden'>
                <motion.div
                    aria-hidden
                    animate={{ x: [0, 40, -20, 0], y: [0, -30, 30, 0] }}
                    transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
                    className='absolute -top-10 -left-20 w-80 h-80 rounded-full opacity-35 blur-3xl pointer-events-none'
                    style={{
                        background:
                            "radial-gradient(circle, #7F00FF 0%, transparent 70%)",
                    }}
                />
                <motion.div
                    aria-hidden
                    animate={{ x: [0, -30, 30, 0], y: [0, 30, -30, 0] }}
                    transition={{ duration: 24, repeat: Infinity, ease: "easeInOut" }}
                    className='absolute top-10 -right-20 w-96 h-96 rounded-full opacity-25 blur-3xl pointer-events-none'
                    style={{
                        background:
                            "radial-gradient(circle, #FCFA02 0%, transparent 70%)",
                    }}
                />

                <div className='relative mx-auto max-w-5xl px-4 sm:px-6 md:px-8 pt-5 md:pt-8 pb-6 md:pb-10 text-center'>
                    {/* DSPY1 — top-of-page display ad */}
                    <ClientAdSense
                        slot='9729451986'
                        containerClassName='w-full mb-6 md:mb-10 text-left'
                    />

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <div className='inline-flex items-center gap-2 ios-glass px-3 py-1.5 rounded-full text-[10px] md:text-xs text-client-font/85 mb-4'>
                            <HiSparkles style={{ color: "#FCFA02" }} />
                            <span className='tracking-wider uppercase'>
                                Got an idea?
                            </span>
                        </div>
                        <h1 className='text-3xl md:text-5xl lg:text-6xl font-bold text-client-font tracking-tight leading-tight'>
                            Share your{" "}
                            <span
                                style={{
                                    background:
                                        "linear-gradient(90deg, #FCFA02, #FF8AFF, #7F00FF)",
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                    backgroundClip: "text",
                                }}
                            >
                                suggestion
                            </span>
                        </h1>
                        <p className='text-client-font/60 text-sm md:text-base mt-3 max-w-xl mx-auto leading-relaxed'>
                            Have a feature idea, a bug to report, or a prompt category
                            you'd love to see? Drop us a note — we read everything.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* MAIN */}
            <section className='relative w-full px-4 sm:px-6 md:px-8 pb-12 md:pb-20'>
                <div className='mx-auto max-w-5xl grid grid-cols-1 lg:grid-cols-5 gap-5 md:gap-6'>
                    {/* Sidebar features (lg+) */}
                    <motion.aside
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                        className='lg:col-span-2 space-y-3 mt-6 md:mt-8 lg:mt-12'
                    >
                        <div
                            className='ios-glass-dark relative rounded-3xl p-5 md:p-6 overflow-hidden'
                            style={{
                                boxShadow:
                                    "0 14px 36px -14px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.1)",
                            }}
                        >
                            <div
                                aria-hidden
                                className='absolute -top-16 -right-12 w-44 h-44 rounded-full opacity-25 blur-3xl pointer-events-none'
                                style={{
                                    background:
                                        "radial-gradient(circle, #7F00FF 0%, transparent 70%)",
                                }}
                            />
                            <motion.div
                                animate={{ rotate: [0, 8, -8, 0], y: [0, -2, 0] }}
                                transition={{
                                    duration: 3,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                }}
                                className='relative inline-flex w-12 h-12 md:w-14 md:h-14 rounded-2xl items-center justify-center mb-3'
                                style={{
                                    background:
                                        "linear-gradient(135deg, #FCFA02, #FF8AFF, #7F00FF)",
                                    boxShadow:
                                        "0 12px 28px -8px rgba(127,0,255,0.55), inset 0 1px 0 rgba(255,255,255,0.25)",
                                }}
                            >
                                <HiOutlineLightBulb className='text-2xl md:text-3xl text-white' />
                            </motion.div>
                            <h3 className='relative text-client-font font-bold text-lg md:text-xl tracking-tight'>
                                Your voice shapes us
                            </h3>
                            <p className='relative text-client-font/60 text-sm mt-2 leading-relaxed'>
                                We respond within{" "}
                                <span className='text-client-title font-semibold'>
                                    1–2 days
                                </span>
                                . Every message reaches a real person on our team.
                            </p>
                        </div>

                        <div className='space-y-2.5'>
                            {FEATURES.map((f, i) => {
                                const Icon = f.icon;
                                return (
                                    <motion.div
                                        key={f.title}
                                        initial={{ opacity: 0, x: -16 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.4, delay: 0.1 + i * 0.08 }}
                                        className='flex items-start gap-3 rounded-2xl p-3.5'
                                        style={{
                                            background: "rgba(255,255,255,0.03)",
                                            border: "1px solid rgba(255,255,255,0.08)",
                                            backdropFilter: "blur(10px)",
                                        }}
                                    >
                                        <div
                                            className='w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0'
                                            style={{
                                                background:
                                                    "linear-gradient(135deg, rgba(127,0,255,0.3), rgba(252,250,2,0.12))",
                                                border: "1px solid rgba(255,255,255,0.12)",
                                            }}
                                        >
                                            <Icon
                                                className='text-base'
                                                style={{ color: "#FCFA02" }}
                                            />
                                        </div>
                                        <div className='min-w-0'>
                                            <h4 className='text-client-font font-semibold text-sm leading-tight'>
                                                {f.title}
                                            </h4>
                                            <p className='text-client-font/55 text-[11px] md:text-xs leading-snug mt-0.5'>
                                                {f.desc}
                                            </p>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </motion.aside>

                    {/* Form */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.55, delay: 0.15 }}
                        className='lg:col-span-3'
                    >
                        <div
                            className='relative rounded-3xl p-5 md:p-8 overflow-hidden'
                            style={{
                                background:
                                    "linear-gradient(165deg, rgba(28,18,48,0.7), rgba(10,8,16,0.85))",
                                backdropFilter: "blur(22px) saturate(180%)",
                                WebkitBackdropFilter: "blur(22px) saturate(180%)",
                                border: "1px solid rgba(255,255,255,0.14)",
                                boxShadow:
                                    "0 24px 60px -16px rgba(127,0,255,0.45), 0 12px 28px -10px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.14)",
                            }}
                        >
                            <motion.div
                                aria-hidden
                                animate={{ rotate: 360 }}
                                transition={{
                                    duration: 50,
                                    repeat: Infinity,
                                    ease: "linear",
                                }}
                                className='absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full opacity-20 blur-3xl pointer-events-none'
                                style={{
                                    background:
                                        "conic-gradient(from 0deg, #7F00FF, #FF8AFF, #FCFA02, #7F00FF)",
                                }}
                            />

                            {sent ? (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{
                                        type: "spring",
                                        stiffness: 220,
                                        damping: 20,
                                    }}
                                    className='relative flex flex-col items-center text-center py-6 md:py-10'
                                >
                                    <motion.div
                                        animate={{
                                            scale: [1, 1.08, 1],
                                        }}
                                        transition={{
                                            duration: 2,
                                            repeat: Infinity,
                                            ease: "easeInOut",
                                        }}
                                        className='w-16 h-16 md:w-20 md:h-20 rounded-2xl flex items-center justify-center mb-4'
                                        style={{
                                            background:
                                                "linear-gradient(135deg, #22c55e, #16a34a)",
                                            boxShadow:
                                                "0 16px 36px -8px rgba(34,197,94,0.55), inset 0 1px 0 rgba(255,255,255,0.25)",
                                        }}
                                    >
                                        <FiCheck className='text-3xl md:text-4xl text-white' />
                                    </motion.div>
                                    <h3 className='text-2xl md:text-3xl font-bold text-client-font tracking-tight'>
                                        Thank you!
                                    </h3>
                                    <p className='text-client-font/65 text-sm md:text-base mt-2 max-w-md'>
                                        Your suggestion has been received. We'll get back
                                        to you within 1–2 business days.
                                    </p>
                                    <div className='flex flex-wrap justify-center gap-2 mt-6'>
                                        <motion.button
                                            whileHover={{ y: -2, scale: 1.04 }}
                                            whileTap={{ scale: 0.96 }}
                                            onClick={() => setSent(false)}
                                            className='ios-glass-btn inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-client-font text-sm font-medium'
                                        >
                                            Send another
                                        </motion.button>
                                        <motion.button
                                            whileHover={{ y: -2, scale: 1.04 }}
                                            whileTap={{ scale: 0.96 }}
                                            onClick={() => navigate("/")}
                                            className='inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white font-semibold text-sm'
                                            style={{
                                                background:
                                                    "linear-gradient(135deg, #7F00FF, #FF8AFF)",
                                                boxShadow:
                                                    "0 12px 28px -8px rgba(127,0,255,0.55), inset 0 1px 0 rgba(255,255,255,0.25)",
                                            }}
                                        >
                                            Back home
                                            <FiArrowRight />
                                        </motion.button>
                                    </div>
                                </motion.div>
                            ) : (
                                <form
                                    onSubmit={handleSubmit}
                                    className='relative flex flex-col gap-4'
                                >
                                    <div className='flex items-center gap-2 mb-1'>
                                        <div
                                            className='w-1 h-6 rounded-full'
                                            style={{
                                                background:
                                                    "linear-gradient(180deg, #7F00FF, #FCFA02)",
                                            }}
                                        />
                                        <h3 className='text-client-font font-bold text-base md:text-lg tracking-tight'>
                                            Tell us what's on your mind
                                        </h3>
                                    </div>

                                    {/* Name */}
                                    <div>
                                        <label className='block text-client-font/65 text-[10px] md:text-xs uppercase tracking-[0.18em] font-semibold mb-1.5 ml-1'>
                                            Name
                                        </label>
                                        <div
                                            className='flex items-center gap-2 rounded-2xl px-3 md:px-4'
                                            style={{
                                                background:
                                                    "rgba(255,255,255,0.04)",
                                                border:
                                                    "1px solid rgba(255,255,255,0.1)",
                                                backdropFilter: "blur(10px)",
                                            }}
                                        >
                                            <FiUser
                                                className='text-base flex-shrink-0'
                                                style={{
                                                    color: "rgba(255,255,255,0.55)",
                                                }}
                                            />
                                            <input
                                                type='text'
                                                name='name'
                                                value={sendData.name}
                                                onChange={handleChange}
                                                placeholder='Your name'
                                                maxLength={50}
                                                className='flex-1 bg-transparent outline-none border-0 text-client-font placeholder:text-client-font/35 py-2.5 md:py-3 text-sm md:text-base'
                                            />
                                        </div>
                                    </div>

                                    {/* Email */}
                                    <div>
                                        <label className='block text-client-font/65 text-[10px] md:text-xs uppercase tracking-[0.18em] font-semibold mb-1.5 ml-1'>
                                            Email
                                        </label>
                                        <div
                                            className='flex items-center gap-2 rounded-2xl px-3 md:px-4'
                                            style={{
                                                background:
                                                    "rgba(255,255,255,0.04)",
                                                border:
                                                    "1px solid rgba(255,255,255,0.1)",
                                                backdropFilter: "blur(10px)",
                                            }}
                                        >
                                            <FiMail
                                                className='text-base flex-shrink-0'
                                                style={{
                                                    color: "rgba(255,255,255,0.55)",
                                                }}
                                            />
                                            <input
                                                type='email'
                                                name='email'
                                                value={sendData.email}
                                                onChange={handleChange}
                                                placeholder='you@example.com'
                                                className='flex-1 bg-transparent outline-none border-0 text-client-font placeholder:text-client-font/35 py-2.5 md:py-3 text-sm md:text-base'
                                            />
                                        </div>
                                    </div>

                                    {/* Message */}
                                    <div>
                                        <div className='flex items-center justify-between mb-1.5'>
                                            <label className='block text-client-font/65 text-[10px] md:text-xs uppercase tracking-[0.18em] font-semibold ml-1'>
                                                Message
                                            </label>
                                            <span
                                                className='text-[10px] md:text-xs tabular-nums font-medium'
                                                style={{
                                                    color:
                                                        charCount > 230
                                                            ? "#FCFA02"
                                                            : "rgba(255,255,255,0.45)",
                                                }}
                                            >
                                                {charCount}/250
                                            </span>
                                        </div>
                                        <div
                                            className='flex items-start gap-2 rounded-2xl px-3 md:px-4 py-2.5 md:py-3'
                                            style={{
                                                background:
                                                    "rgba(255,255,255,0.04)",
                                                border:
                                                    "1px solid rgba(255,255,255,0.1)",
                                                backdropFilter: "blur(10px)",
                                            }}
                                        >
                                            <FiMessageSquare
                                                className='text-base flex-shrink-0 mt-1'
                                                style={{
                                                    color: "rgba(255,255,255,0.55)",
                                                }}
                                            />
                                            <textarea
                                                name='msg'
                                                value={sendData.msg}
                                                onChange={handleChange}
                                                placeholder='Share your idea, feedback, or bug report…'
                                                rows={4}
                                                maxLength={250}
                                                className='flex-1 bg-transparent outline-none border-0 text-client-font placeholder:text-client-font/35 text-sm md:text-base resize-none leading-relaxed'
                                            />
                                        </div>
                                        {/* Char progress bar */}
                                        <div
                                            className='mt-1.5 h-1 rounded-full overflow-hidden mx-1'
                                            style={{
                                                background:
                                                    "rgba(255,255,255,0.06)",
                                            }}
                                        >
                                            <motion.div
                                                className='h-full rounded-full'
                                                animate={{ width: `${charPct}%` }}
                                                transition={{ duration: 0.2 }}
                                                style={{
                                                    background:
                                                        charCount > 230
                                                            ? "linear-gradient(90deg, #FCFA02, #FF8AFF)"
                                                            : "linear-gradient(90deg, #7F00FF, #FF8AFF)",
                                                    boxShadow:
                                                        "0 0 8px rgba(127,0,255,0.55)",
                                                }}
                                            />
                                        </div>
                                    </div>

                                    {/* Submit */}
                                    <motion.button
                                        type='submit'
                                        disabled={loading}
                                        whileHover={!loading ? { y: -2, scale: 1.02 } : {}}
                                        whileTap={!loading ? { scale: 0.97 } : {}}
                                        className='relative mt-2 px-5 py-3 md:py-3.5 rounded-2xl text-white font-bold text-sm md:text-base inline-flex items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-70 overflow-hidden'
                                        style={{
                                            background:
                                                "linear-gradient(135deg, #7F00FF, #FF8AFF)",
                                            boxShadow:
                                                "0 16px 36px -8px rgba(127,0,255,0.6), inset 0 1px 0 rgba(255,255,255,0.25)",
                                        }}
                                    >
                                        {!loading && (
                                            <motion.div
                                                aria-hidden
                                                animate={{ x: ["-120%", "120%"] }}
                                                transition={{
                                                    duration: 2.4,
                                                    repeat: Infinity,
                                                    ease: "easeInOut",
                                                    repeatDelay: 1.5,
                                                }}
                                                className='absolute inset-y-0 w-1/2 pointer-events-none'
                                                style={{
                                                    background:
                                                        "linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.55) 50%, transparent 70%)",
                                                    filter: "blur(2px)",
                                                }}
                                            />
                                        )}
                                        {loading ? (
                                            <>
                                                <motion.div
                                                    animate={{ rotate: 360 }}
                                                    transition={{
                                                        duration: 0.8,
                                                        repeat: Infinity,
                                                        ease: "linear",
                                                    }}
                                                    className='relative w-5 h-5 rounded-full border-[2.5px] border-white/30 border-t-white'
                                                />
                                                <span className='relative tracking-wide uppercase text-sm'>
                                                    Sending…
                                                </span>
                                            </>
                                        ) : (
                                            <>
                                                <FiSend className='relative' />
                                                <span className='relative tracking-wide uppercase'>
                                                    Send Suggestion
                                                </span>
                                                <FiArrowRight className='relative' />
                                            </>
                                        )}
                                    </motion.button>
                                </form>
                            )}
                        </div>
                    </motion.div>
                </div>
            </section>

            <ClientFooter />
        </div>
    );
};

export default ClientSuggestion;
